import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { getAuth } from "~/providers/store/auth";
import { collectActions } from "~/providers/store/collect/actions";
import { toast } from "~/components/hooks/use-toast";
import { T } from "@repo/transifex";
import { childrenKeys } from "~/api/children/keys";
import { collectOrderKeys } from "~/api/collect/keys";
import { DisplayErrorMessage } from "~/components/display-error-message";

import { createPreOrders, type CreatePreOrdersArgs } from "../requests";

import { reportPreOrderDepositRejection } from "./report-pre-order-deposit-rejection";

export function useCreatePreOrders() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      childId,
      groupName,
      basket
    }: Omit<CreatePreOrdersArgs, "tillId">) => {
      const { tillId } = getAuth();
      if (!tillId) {
        throw new Error("Till ID missing");
      }

      return createPreOrders({ tillId, childId, groupName, basket });
    },
    onSuccess: (_, { childId }) => {
      toast({
        title: <T _str="Order created" />,
        variant: "success",
        description: <T _str="Order has been created successfully" />
      });

      // Refresh the students list and this order after creating a pre-order.
      queryClient.invalidateQueries({
        queryKey: childrenKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: collectOrderKeys.detail({ childId })
      });

      navigate("/collect");
      collectActions.resetFilters();
    },
    onError: (error, { basket }) => {
      reportPreOrderDepositRejection(error, basket);

      toast({
        title: <T _str="Order creation failed" />,
        variant: "destructive",
        description: (
          <DisplayErrorMessage
            error={error}
            fallback={<T _str="Could not create order" />}
          />
        )
      });
    }
  });
}
